import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, LogOut } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

// ═══════════════════════════════════════════════════════════════════════════════
// PRIMER INGRESO con contraseña temporal (COL.10, PASO 4).
//
// 🔴 Esta pantalla vive FUERA de `AppLayout`: sin menú, sin badge de WhatsApp, sin
//    nada. No es estética -- una sesión abierta con contraseña temporal no sirve para
//    nada salvo cambiarla, y el servidor lo impone: el token lleva el claim `pwd_temp`
//    y `get_current_user` rechaza cualquier ruta fuera de `_RUTAS_CON_PASSWORD_TEMPORAL`
//    (`/auth/password`, `/auth/me` y `/auth/logout`). Pintar el menú aquí ofrecería
//    catorce puertas que devuelven 403.
//
// ⚠️ Consecuencia de esa lista corta, y por eso esta pantalla no pide `/clinics/mine`
//    ni nada más: cualquier otra llamada devolvería 403 y se leería como un fallo.
// ═══════════════════════════════════════════════════════════════════════════════

export default function CambiarPassword() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const esTemporal = !!user?.password_temporal;

  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetida, setRepetida] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  // El mínimo lo impone el backend (`Field(min_length=8)`). Aquí se dice ANTES de
  // mandar para no gastarle a nadie un viaje, pero quien manda es el servidor.
  const cortita = nueva.length > 0 && nueva.length < 8;
  const noCoincide = repetida.length > 0 && nueva !== repetida;
  const puede = nueva.length >= 8 && nueva === repetida && (esTemporal || actual) && !enviando;

  const guardar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/auth/password", {
        // 🔴 La actual se exige SALVO cuando la vigente es temporal, y esa excepción la
        //    decide el backend leyendo la fila, no este formulario: aquí sólo se manda
        //    lo que hay. Un frontend manipulado no se salta nada.
        actual: esTemporal ? null : actual,
        nueva,
      });
      toast.success("Contraseña cambiada. Vuelve a entrar con la nueva.");
      // Se cierra la sesión a propósito: el token vigente todavía lleva `pwd_temp`, así
      // que seguiría sin servir para nada. Volver a entrar es lo que emite uno limpio.
      logout();
      nav("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail ?? err?.response?.data));
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <div className="flex items-center gap-2 text-slate-900">
          <KeyRound className="w-5 h-5" />
          <h1 className="font-display text-xl font-extrabold">
            {esTemporal ? "Elige tu contraseña" : "Cambiar contraseña"}
          </h1>
        </div>

        {esTemporal && (
          <p className="text-sm text-slate-600 mt-2" data-testid="aviso-temporal">
            Entraste con una contraseña temporal. Antes de usar el panel tienes que elegir
            una tuya: la temporal deja de servir en cuanto la cambies.
          </p>
        )}

        <form onSubmit={guardar} className="space-y-3 mt-5">
          {!esTemporal && (
            <Campo label="Contraseña actual" value={actual} onChange={setActual} />
          )}
          <Campo label="Contraseña nueva" value={nueva} onChange={setNueva} />
          {cortita && <p className="text-xs text-rose-700">Mínimo 8 caracteres.</p>}
          <Campo label="Repítela" value={repetida} onChange={setRepetida} />
          {noCoincide && <p className="text-xs text-rose-700">Las dos no coinciden.</p>}

          {error && (
            <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900" data-testid="error-password">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!puede}
            className="w-full px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-40"
            data-testid="btn-guardar-password"
          >
            {enviando ? "Guardando…" : "Guardar y entrar"}
          </button>
        </form>

        <button
          onClick={() => { logout(); nav("/login", { replace: true }); }}
          className="mt-5 inline-flex items-center gap-1.5 text-sm text-slate-500"
        >
          <LogOut className="w-4 h-4" /> Salir
        </button>
      </div>
    </div>
  );
}

function Campo({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm"
      />
    </label>
  );
}
